import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button, type ButtonProps } from "~/components/button";

interface ProductNavigationButtonProps {
  productId?: string;
  activeTab: string;
  projectId: string;
  label: string;
  icon: ButtonProps["icon"];
  iconPosition: ButtonProps["iconPosition"];
  analyticsId: string;
  testId: string;
}

function ProductNavigationButton({
  productId,
  activeTab,
  projectId,
  label,
  icon,
  iconPosition,
  analyticsId,
  testId,
}: ProductNavigationButtonProps) {
  const button = (
    <Button
      variant="tertiary"
      disabled={!productId}
      analyticsId={analyticsId}
      icon={icon}
      iconPosition={iconPosition}
      spanClassName="hidden lg:block"
      label={label}
      size="small"
    />
  );

  if (!productId) {
    return button;
  }

  return (
    <Link
      data-testid={testId}
      to="/projects/$projectId/product/$productId"
      params={{ projectId, productId }}
      search={{ tab: activeTab }}
    >
      {button}
    </Link>
  );
}

interface ProductNavigationBarButtonsProps {
  projectId?: string;
  productId?: string;
  previousProductId?: string;
  nextProductId?: string;
  activeTab: string;
}

export const ProductNavigationBarButtons = ({
  projectId,
  productId,
  previousProductId,
  nextProductId,
  activeTab,
}: ProductNavigationBarButtonsProps) => {
  const { t } = useTranslation(["productDetails"]);

  if (!projectId || !productId) {
    return null;
  }

  return (
    <div className="gap-4 flex flex-row">
      <ProductNavigationButton
        productId={previousProductId}
        activeTab={activeTab}
        projectId={projectId}
        label={t("productDetails:header.previous")}
        icon="arrow-left"
        iconPosition="left"
        analyticsId="button_previous_product"
        testId="previous-product-button"
      />
      <ProductNavigationButton
        productId={nextProductId}
        activeTab={activeTab}
        projectId={projectId}
        label={t("productDetails:header.next")}
        icon="arrow-right"
        iconPosition="right"
        analyticsId="button_next_product"
        testId="next-product-button"
      />
    </div>
  );
};
